import { useState, useEffect, useCallback } from 'react';
import { 
  listComments, 
  createThread, 
  resolveThread, 
  deleteThread,
  addReply
} from '../services/audioCommentService';
import type { AudioComment } from '../services/audioCommentService';
import { searchUsers } from '../services/userService';
import type { UserSearchResult } from '../services/userService';
import { logInfo, logError } from '../services/logtail';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface UseAudioCommentsProps {
  trackId: string;
  fileId: string;
  canComment: boolean;
  canView: boolean;
}

interface UseAudioCommentsReturn {
  comments: AudioComment[];
  loading: boolean;
  error: string | null;
  userMap: Record<string, UserSearchResult>;
  createComment: (timestampMs: number, text: string) => Promise<void>;
  addCommentReply: (threadId: string, text: string) => Promise<void>;
  resolveComment: (threadId: string, resolved: boolean) => Promise<void>;
  deleteComment: (threadId: string) => Promise<void>;
  refreshComments: () => Promise<void>;
}

export const useAudioComments = ({ 
  trackId, 
  fileId, 
  canComment, 
  canView 
}: UseAudioCommentsProps): UseAudioCommentsReturn => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<AudioComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userMap, setUserMap] = useState<Record<string, UserSearchResult>>({});

  // Load user details for comment authors
  const loadUserDetails = useCallback(async (comments: AudioComment[]) => {
    const userIds = new Set<string>();
    
    // Collect all unique user IDs from comments and replies
    comments.forEach(comment => {
      userIds.add(comment.authorUserId);
      comment.replies.forEach(reply => {
        userIds.add(reply.authorUserId);
      });
    });

    if (userIds.size === 0) return;

    try {
      logInfo('Loading user details for comment authors', { 
        userIds: Array.from(userIds),
        totalComments: comments.length 
      });

      const userPromises = Array.from(userIds).map(async (userId) => {
        try {
          const users = await searchUsers(userId);
          return { userId, user: users[0] };
        } catch (error) {
          logError('Failed to load user details', { userId, error });
          return { userId, user: null };
        }
      });

      const userResults = await Promise.all(userPromises);
      
      setUserMap(prevUserMap => {
        const newUserMap = { ...prevUserMap };
        userResults.forEach(({ userId, user }) => {
          if (user) {
            newUserMap[userId] = user;
          }
        });
        return newUserMap;
      });
    } catch (error) {
      logError('Failed to load user details', { error });
    }
  }, []);

  // Load comments
  const loadComments = useCallback(async () => {
    if (!canView || !trackId || !fileId) {
      setComments([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      logInfo('Loading audio comments', { trackId, fileId });
      const commentsData = await listComments(trackId, fileId);
      setComments(commentsData || []); // Handle null response from backend
      
      // Load user details for comment authors (non-blocking)
      loadUserDetails(commentsData || []).catch(error => {
        logError('Failed to load user details in background', { error });
      });
      
      logInfo('Audio comments loaded successfully', { count: (commentsData || []).length });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load comments';
      logError('Failed to load audio comments', { trackId, fileId, error: errorMessage });
      setError(errorMessage);
      toast.error(t('audioComments.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [trackId, fileId, canView]); // Removed 't' to prevent infinite loops

  // Load comments on mount and when dependencies change
  useEffect(() => {
    loadComments();
  }, [loadComments]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setComments([]);
      setUserMap({});
      setError(null);
    };
  }, []);

  // Create a new comment
  const createComment = useCallback(async (timestampMs: number, text: string) => {
    if (!canComment) {
      toast.error(t('audioComments.noPermission'));
      return;
    }

    try {
      logInfo('Creating audio comment', { trackId, fileId, timestampMs });
      const newComment = await createThread(trackId, fileId, timestampMs, text);
      setComments(prev => [...prev, newComment].sort((a, b) => a.timestampMs - b.timestampMs));
      toast.success(t('audioComments.commentCreated'));
      logInfo('Audio comment created successfully', { threadId: newComment.threadId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create comment';
      logError('Failed to create audio comment', { trackId, fileId, error: errorMessage });
      toast.error(t('audioComments.errorCreating'));
      throw err;
    }
  }, [trackId, fileId, canComment, t]);

  // Resolve/unresolve a comment
  const resolveComment = useCallback(async (threadId: string, resolved: boolean) => {
    if (!canComment) {
      toast.error(t('audioComments.noPermission'));
      return;
    }

    try {
      logInfo('Resolving audio comment', { trackId, fileId, threadId, resolved });
      await resolveThread(trackId, fileId, threadId, resolved);
      setComments(prev => 
        prev.map(comment => 
          comment.threadId === threadId 
            ? { ...comment, resolved } 
            : comment
        )
      );
      toast.success(resolved ? t('audioComments.commentResolved') : t('audioComments.commentUnresolved'));
      logInfo('Audio comment resolved successfully', { threadId, resolved });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resolve comment';
      logError('Failed to resolve audio comment', { trackId, threadId, error: errorMessage });
      toast.error(t('audioComments.errorResolving'));
      throw err;
    }
  }, [trackId, canComment, t]);

  // Add a reply to a comment
  const addCommentReply = useCallback(async (threadId: string, text: string) => {
    if (!canComment) {
      toast.error(t('audioComments.noPermission'));
      return;
    }

    try {
      logInfo('Adding reply to audio comment', { trackId, fileId, threadId });
      await addReply(trackId, fileId, threadId, text);
      // Reload comments to get the updated thread with new reply
      await loadComments();
      toast.success(t('audioComments.replyAdded'));
      logInfo('Reply added successfully', { threadId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add reply';
      logError('Failed to add reply to audio comment', { trackId, threadId, error: errorMessage });
      toast.error(t('audioComments.errorAddingReply'));
      throw err;
    }
  }, [trackId, fileId, canComment, loadComments, t]);

  // Delete a comment
  const deleteComment = useCallback(async (threadId: string) => {
    if (!canComment) {
      toast.error(t('audioComments.noPermission'));
      return;
    }

    try {
      logInfo('Deleting audio comment', { trackId, fileId, threadId });
      await deleteThread(trackId, fileId, threadId);
      setComments(prev => prev.filter(comment => comment.threadId !== threadId));
      toast.success(t('audioComments.commentDeleted'));
      logInfo('Audio comment deleted successfully', { threadId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete comment';
      logError('Failed to delete audio comment', { trackId, threadId, error: errorMessage });
      toast.error(t('audioComments.errorDeleting'));
      throw err;
    }
  }, [trackId, canComment, t]);

  return {
    comments,
    loading,
    error,
    userMap,
    createComment,
    addCommentReply,
    resolveComment,
    deleteComment,
    refreshComments: loadComments,
  };
};

